/**
 * TourSpotlight
 *
 * Full-viewport SVG overlay that darkens the screen and cuts a rounded-rect
 * hole over the spotlighted DOM element. Gold dashed border on the cutout.
 * Smoothly transitions between targets via CSS transition on the rect position.
 */

import { useState, useEffect, useCallback } from 'react';

interface TourSpotlightProps {
  /** CSS selector of the element to highlight (e.g. '[data-tour="map-canvas"]') */
  selector?: string;
  /** Click anywhere on the dark overlay to advance */
  onClick: () => void;
}

const PADDING = 12;
const BORDER_RADIUS = 8;

export function TourSpotlight({ selector, onClick }: TourSpotlightProps) {
  const [rect, setRect] = useState<DOMRect | null>(null);

  const measure = useCallback(() => {
    if (!selector) {
      setRect(null);
      return;
    }
    const el = document.querySelector(selector);
    if (el) {
      setRect(el.getBoundingClientRect());
    } else {
      setRect(null);
    }
  }, [selector]);

  useEffect(() => {
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [measure]);

  // SVG cutout dimensions
  const x = rect ? rect.left - PADDING : 0;
  const y = rect ? rect.top - PADDING : 0;
  const w = rect ? rect.width + PADDING * 2 : 0;
  const h = rect ? rect.height + PADDING * 2 : 0;

  return (
    <div
      onClick={onClick}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 2500,
        cursor: 'pointer',
      }}
    >
      <svg
        width="100%"
        height="100%"
        style={{ position: 'absolute', inset: 0 }}
      >
        <defs>
          <mask id="tour-spotlight-mask">
            {/* White = visible (dark overlay) */}
            <rect width="100%" height="100%" fill="white" />
            {/* Black = transparent (hole) */}
            {rect && (
              <rect
                x={x}
                y={y}
                width={w}
                height={h}
                rx={BORDER_RADIUS}
                ry={BORDER_RADIUS}
                fill="black"
                style={{ transition: 'all 400ms ease-out' }}
              />
            )}
          </mask>
        </defs>

        {/* Dark overlay with mask hole */}
        <rect
          width="100%"
          height="100%"
          fill="rgba(0, 0, 0, 0.75)"
          mask="url(#tour-spotlight-mask)"
        />

        {/* Gold dashed border around the cutout */}
        {rect && (
          <rect
            x={x}
            y={y}
            width={w}
            height={h}
            rx={BORDER_RADIUS}
            ry={BORDER_RADIUS}
            fill="none"
            stroke="#d4af37"
            strokeWidth="2"
            strokeDasharray="8 4"
            style={{ transition: 'all 400ms ease-out' }}
          />
        )}
      </svg>
    </div>
  );
}
